import { motion } from 'framer-motion'
import { 
  LayoutDashboard, 
  Pencil, 
  Printer, 
  Store, 
  Settings, 
  HelpCircle,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  FolderOpen,
  Wifi,
  Wand2,
} from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import BetaBadge from '@/components/ui/BetaBadge'
import type { ViewType } from '@/pages/dashboard/DashboardApp'

interface SidebarProps {
  currentView: ViewType
  onViewChange: (view: ViewType) => void
}

const navItems = [
  { id: 'dashboard' as ViewType, label: 'Dashboard', icon: LayoutDashboard },
  { id: 'projects' as ViewType, label: 'Projects', icon: FolderOpen },
  { id: 'text-to-3d' as ViewType, label: 'Text to 3D', icon: Wand2 },
  { id: 'design' as ViewType, label: 'Design Studio', icon: Pencil },
  { id: 'printers' as ViewType, label: 'Printers', icon: Wifi },
  { id: 'print' as ViewType, label: 'Print Center', icon: Printer },
  { id: 'marketplace' as ViewType, label: 'MakerHub', icon: Store },
]

const bottomItems = [
  { id: 'settings' as ViewType, label: 'Settings', icon: Settings },
  { id: 'help' as const, label: 'Help & Support', icon: HelpCircle },
]

export default function Sidebar({ currentView }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false)

  return (
    <aside
      style={{ width: collapsed ? 72 : 240 }}
      className="h-full bg-surface-900 border-r border-surface-800 flex flex-col z-20"
    >
      {/* Logo */}
      <div className="h-16 flex items-center px-4 border-b border-surface-800">
        <div className="flex items-center gap-3 overflow-hidden">
          <div className="w-10 h-10 rounded-xl gradient-ai flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-white">3DMakerly</h1>
                <BetaBadge size="sm" />
              </div>
              <p className="text-xs text-surface-400 -mt-0.5">AI-Native CAD</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = currentView === item.id
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                window.location.hash = item.id
              }}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 cursor-pointer',
                'hover:bg-surface-800',
                isActive && 'bg-primary-600/20 text-primary-400 hover:bg-primary-600/30'
              )}
            >
              <item.icon className={cn(
                'w-5 h-5 flex-shrink-0',
                isActive ? 'text-primary-400' : 'text-surface-400'
              )} />
              {!collapsed && (
                <span className={cn(
                  'text-sm font-medium truncate',
                  isActive ? 'text-primary-400' : 'text-surface-300'
                )}>
                  {item.label}
                </span>
              )}
              {isActive && !collapsed && (
                <motion.div
                  layoutId="activeIndicator"
                  className="ml-auto w-1.5 h-1.5 rounded-full bg-primary-400"
                />
              )}
            </button>
          )
        })}
      </nav>

      {/* Bottom Items */}
      <div className="py-4 px-3 space-y-1 border-t border-surface-800">
        {bottomItems.map((item) => {
          const isActive = item.id === 'settings' && currentView === 'settings'
          const isSettings = item.id === 'settings'
          return (
            <button
              key={item.id}
              type="button"
              onClick={() => {
                if (isSettings) window.location.hash = 'settings'
              }}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors cursor-pointer',
                isActive 
                  ? 'bg-primary-600/20 text-primary-400' 
                  : 'text-surface-400 hover:bg-surface-800 hover:text-surface-200'
              )}
            >
              <item.icon className="w-5 h-5 flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm font-medium truncate">{item.label}</span>
              )}
            </button>
          )
        })}
      </div>

      {/* Collapse Toggle */}
      <div className="p-3 border-t border-surface-800">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-surface-400 hover:bg-surface-800 hover:text-surface-200 transition-colors"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <>
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm">Collapse</span>
            </>
          )}
        </button>
      </div>
    </aside>
  )
}
